import React, { useCallback } from 'react';
import get from 'lodash/get';
import compact from 'lodash/compact';

import { PaymentAmountField } from '../../components/Healthboard/Components/PaymentAmountField';
import { VisitDocuments } from '../../components/Healthboard/Components/VisitDocuments';
import { VisitSurveyList } from '../../components/Healthboard/Components/VisitSurveyListView';
import { formatHours, humanizeLocalDate } from './dateFilter';
import { formatAmount } from './formatAmount';
import {
  APPOINTMENT_STATUSES,
  EIR_APPOINTMENT_TYPES,
  PAYMENT_STATUSES,
} from '../../constants';
import { IDS } from '../../constants/ids';
import { NotificationConsentView } from '../../components/Healthboard/Components/VisitConsents/NotificationConsentView';
import { VisitConsents } from '../../components/Healthboard/Components/VisitConsents';
import { getFullAdress } from '../../components/AppointmentForm/helpers/getFullLocationAdress';
import VisitPrescriptions from '../../components/Healthboard/Components/VisitPrescriptions';

import { COLORS } from '../../constants/styles';

export const ROWS = {
  DATE: 'date',
  PROVIDER: 'provider',
  REASON: 'reason',
  PRIVACY: 'privacy',
  PAYMENT: 'payment',
  EDITABLE_PAYMENT: 'editable-payment',
  DOCUMENTS: 'documents',
  SURVEYS: 'surveys',
  CONSENTS: 'consents',
  NOTIFICATION_CONSENT: 'notification-consent',
  LOCATION: 'location',
  DATE_AND_TIME: 'date-and-time',
  PRESCRIPTIONS: 'prescriptions',
  ADDITIONAL_INFO: 'additional-info',
};

function shouldShowAdditionalDuration({
  additionalDuration,
  status,
  visitTypeCode,
}) {
  const isEirType = EIR_APPOINTMENT_TYPES.includes(visitTypeCode);
  return (
    additionalDuration &&
    ((!isEirType && status !== APPOINTMENT_STATUSES.COMPLETED) ||
      (isEirType && status === APPOINTMENT_STATUSES.COMPLETED))
  );
}

export function useOverviewRowsBuilder({ formatMessage, isInlineVisit }) {
  let makeLocationRow;
  let makeDateAndTimeRow;

  const makeDateRow = useCallback(
    ({
      time,
      additionalDuration,
      status,
      icsUrl,
      icsAllowed,
      visitTypeCode,
    }) => {
      const formattedTime = formatHours(time);

      const timeText = shouldShowAdditionalDuration({
        additionalDuration,
        status,
        visitTypeCode,
      })
        ? `${formattedTime} (${additionalDuration})`
        : formattedTime;

      return {
        id: ROWS.DATE,
        row: [
          {
            title: formatMessage({ id: 'common.date' }),
            text: humanizeLocalDate(time),
            ...(icsAllowed &&
              icsUrl && {
                icsUrlTitle: formatMessage({ id: 'general.addToCalendar' }),
                icsUrl,
              }),
          },
          {
            title: formatMessage({ id: 'dashboard.table.time' }),
            text: timeText,
          },
        ],
      };
    },
    [],
  );

  const makeProviderRow = useCallback(
    ({
      provider,
      service,
      renderCell,
      hasLocation,
      isVirtualVisit,
      isForbiddenMap,
    }) => {
      let secondCell = service
        ? {
            title: formatMessage({ id: 'dashboard.filter.service' }),
            text: get(service, 'name') || '-',
          }
        : {
            title: formatMessage({ id: 'common.phone' }),
            text: get(provider, 'location.phone') || '-',
          };

      if (typeof renderCell === 'function') {
        secondCell = renderCell();
      }

      return {
        id: ROWS.PROVIDER,
        row: compact([
          hasLocation && {
            title: formatMessage({ id: 'dashboard.filter.location' }),
            text: [
              get(provider, 'location.name'),
              `${
                !isVirtualVisit
                  ? `${get(provider, 'location.address')}, ${get(
                      provider,
                      'location.city',
                    )}`
                  : ''
              }`,
            ].join('\n'),
            ...(!isForbiddenMap && {
              location: formatMessage({ id: 'general.viewOnMap' }),
            }),
          },
          secondCell,
        ]),
      };
    },
    [],
  );

  const makeReasonRow = useCallback(
    reason => ({
      id: ROWS.REASON,
      row: [
        {
          title: formatMessage({ id: 'fields.reasonType' }),
          text: get(reason, 'type') || '-',
        },
        {
          title: formatMessage({ id: 'fields.reasonNote' }),
          text: get(reason, 'note') || '-',
        },
      ],
    }),
    [],
  );

  const makePrivacyRow = useCallback(
    consentInfo => ({
      id: ROWS.PRIVACY,
      row: [
        {
          title: formatMessage({ id: 'common.documents' }),
          text: formatMessage({ id: 'appointment.form.patientDoc' }),
          extra: formatMessage({ id: 'common.complete' }),
        },
        {
          title: '',
          text: get(consentInfo, 'title') || '-',
          extra: get(consentInfo, 'active')
            ? formatMessage({ id: 'common.complete' })
            : '-',
        },
      ],
    }),
    [],
  );

  const makePaymentRow = useCallback(price => {
    const text =
      price === null
        ? formatMessage({
            id: 'healthboard.appointments.paymentStatus.pendingPaymentInfo',
          })
        : formatMessage(
            { id: 'healthboard.appointments.paymentStatus.tapToPay' },
            { price: Number(price).toFixed(2) },
          );

    const style =
      price !== null
        ? {
            fontWeight: '600',
            color: COLORS.bluePrimary,
          }
        : {};

    return {
      id: ROWS.PAYMENT,
      row: [
        {
          title: formatMessage({ id: 'appointment.form.costTitle' }),
          text,
          style,
        },
      ],
    };
  }, []);

  const makeDocRow = useCallback(
    ({ providerContext, appointment }) => ({
      id: ROWS.DOCUMENTS,
      row: [
        {
          component: (
            <VisitDocuments
              formatMessage={formatMessage}
              providerContext={providerContext}
              key={IDS.FORM_FIELDS.DOCUMENTS}
              appointment={appointment}
            />
          ),
          fullWidthRow: true,
        },
      ],
    }),
    [],
  );

  const makeSurveyRow = useCallback(
    ({ appointment }) => ({
      id: ROWS.SURVEYS,
      row: [
        {
          component: (
            <VisitSurveyList
              formatMessage={formatMessage}
              key={IDS.FORM_FIELDS.SURVEYS}
              appointment={appointment}
            />
          ),
          fullWidthRow: true,
        },
      ],
    }),
    [],
  );

  const makeNotificationConsentRow = useCallback(
    ({ appointment, providerContext }) => ({
      id: ROWS.NOTIFICATION_CONSENT,
      row: [
        {
          component: (
            <NotificationConsentView
              formatMessage={formatMessage}
              key={IDS.FORM_FIELDS.NOTIFICATION_CONSENTS}
              appointment={appointment}
              isProviderContext={providerContext}
            />
          ),
          fullWidthRow: true,
        },
      ],
    }),
    [],
  );

  const makeConsentRow = useCallback(
    ({ appointment, providerContext }) => ({
      id: ROWS.CONSENTS,
      row: [
        {
          component: (
            <VisitConsents
              formatMessage={formatMessage}
              key={IDS.FORM_FIELDS.CONSENTS}
              appointment={appointment}
              providerContext={providerContext}
            />
          ),
          fullWidthRow: true,
        },
      ],
    }),
    [],
  );

  const makePrescriptionsRow = useCallback(
    prescriptions => ({
      id: ROWS.PRESCRIPTIONS,
      row: [
        {
          component: (
            <VisitPrescriptions
              formatMessage={formatMessage}
              prescriptions={prescriptions}
              key={IDS.FORM_FIELDS.PRESCRIPTIONS}
            />
          ),
          fullWidthRow: true,
        },
      ],
    }),
    [],
  );

  const makeEditablePaymentRow = useCallback(({ appointment, isUpcoming }) => {
    const paymentStatus = get(appointment, 'paymentStatus');
    const isPaid = paymentStatus === PAYMENT_STATUSES.PAID;
    const appointmentId = get(appointment, 'id');

    const statusI18n = isPaid
      ? 'healthboard.appointments.paymentStatus.paid'
      : 'healthboard.appointments.paymentStatus.unpaid';

    const amount = isPaid
      ? get(appointment, 'paidAmount')
      : get(appointment, 'copay');

    return {
      id: ROWS.EDITABLE_PAYMENT,
      row: [
        {
          title: formatMessage({
            id: 'appointment.details.paymentAmount.title',
          }),
          text: `$${formatAmount(amount || 0)}`,
          component: (
            <PaymentAmountField
              formatMessage={formatMessage}
              value={amount}
              isPaid={isPaid}
              isUpcoming={isUpcoming}
              appointmentId={appointmentId}
              appointment={appointment}
            />
          ),
        },
        {
          title: formatMessage({
            id: 'appointment.details.paymentStatus.title',
          }),
          text: formatMessage({ id: statusI18n }),
        },
      ],
    };
  }, []);

  if (isInlineVisit) {
    makeLocationRow = useCallback(
      location => ({
        id: ROWS.LOCATION,
        row: [
          {
            title: formatMessage({ id: 'profile.fields.location' }),
            text: [location.name, getFullAdress(location)].join('\n'),
          },
          {
            title: formatMessage({ id: 'common.hours' }),
            text: `${formatHours(
              location.schedule.metadata.startTime,
            )} - ${formatHours(location.schedule.metadata.endTime)}`,
          },
        ],
      }),
      [],
    );

    makeDateAndTimeRow = useCallback(time => {
      const displayTime = time
        ? `${formatMessage({
            id: 'common.today',
          })}, ${formatHours(time)}`
        : '-';

      return {
        id: ROWS.DATE_AND_TIME,
        row: [
          {
            title: formatMessage({ id: 'dashboard.table.time' }),
            text: displayTime,
          },
        ],
      };
    }, []);
  }

  return {
    makeDateRow,
    makeProviderRow,
    makeReasonRow,
    makePrivacyRow,
    makePaymentRow,
    makeDocRow,
    makeSurveyRow,
    makeEditablePaymentRow,
    makeConsentRow,
    makeNotificationConsentRow,
    makeLocationRow,
    makeDateAndTimeRow,
    makePrescriptionsRow,
  };
}



// WEBPACK FOOTER //
// ./src/utils/appointments/useOverviewRowsBuilder.jsx